import type {
  PortalClubwiseBreakdownRow,
  PortalClubwiseChannel,
  PortalClubwiseChannelSplit,
} from '../../types/portalReport.js';
import { LEAD_CHANNELS } from './classify.js';

// CHANNEL DRILL-DOWN — pure. Splits each channel row of the main report into the
// Sources and Contact Types that make it up.
//
// ⚠️⚠️ AGGREGATES ONLY: the input is (channel × source × contact type) COUNTS.
// ⚠️ SUB-ROWS ALWAYS ADD UP TO THE PARENT: both splits are folds of the same
// rows, so a channel's sources sum to its leads and its contact types sum to its
// leads. ⚠️ Same window as the channel table (the measured months, month-to-date
// included); the server only ever queries those months.

export interface ChannelSplitRow {
  channel: string;
  source: string;
  contactType: string;
  leads: number;
  joined: number;
}

export const NOT_RECORDED = '(not recorded)';
export const REMOVED_LABEL = '(removed)';
export const TOP_N = 8;

/** Belt and braces: values were scrubbed at upload, but a digit run or an "@" never renders. */
export function safeLabel(raw: string): string {
  const v = raw.replace(/\s+/g, ' ').trim();
  if (v === '') return NOT_RECORDED;
  if (v.includes('@') || (v.match(/\d/g)?.length ?? 0) >= 8) return REMOVED_LABEL;
  return v;
}

const pct1 = (n: number, d: number): number | null => (d > 0 ? Math.round((n / d) * 1000) / 10 : null);

function fold(rows: readonly ChannelSplitRow[], key: 'source' | 'contactType'): PortalClubwiseBreakdownRow[] {
  const by = new Map<string, { leads: number; joined: number }>();
  for (const r of rows) {
    const label = safeLabel(r[key]);
    const cur = by.get(label) ?? { leads: 0, joined: 0 };
    cur.leads += r.leads;
    cur.joined += r.joined;
    by.set(label, cur);
  }
  return [...by.entries()]
    .map(([label, v]) => ({ label, leads: v.leads, joined: v.joined, conversionRate: pct1(v.joined, v.leads) }))
    .sort((a, b) => b.leads - a.leads || a.label.localeCompare(b.label));
}

export function buildChannelSplits(
  rows: readonly ChannelSplitRow[],
): Partial<Record<PortalClubwiseChannel, PortalClubwiseChannelSplit>> {
  const out: Partial<Record<PortalClubwiseChannel, PortalClubwiseChannelSplit>> = {};
  for (const channel of LEAD_CHANNELS) {
    const mine = rows.filter((r) => r.channel === channel);
    if (mine.length === 0) continue;
    out[channel] = { sources: fold(mine, 'source'), contactTypes: fold(mine, 'contactType') };
  }
  return out;
}

/** Top `n` by leads; the rest are one "Other values (k)" row that expands to all of them. */
export function foldTop(
  rows: readonly PortalClubwiseBreakdownRow[],
  n = TOP_N,
): { top: PortalClubwiseBreakdownRow[]; rest: PortalClubwiseBreakdownRow[]; other: PortalClubwiseBreakdownRow | null } {
  // A fold of one value would be a row hiding a row: only fold when 2+ are left over.
  if (rows.length <= n + 1) return { top: [...rows], rest: [], other: null };
  const top = rows.slice(0, n);
  const rest = rows.slice(n);
  const leads = rest.reduce((s, r) => s + r.leads, 0);
  const joined = rest.reduce((s, r) => s + r.joined, 0);
  return {
    top,
    rest: [...rest],
    other: { label: `Other values (${rest.length})`, leads, joined, conversionRate: pct1(joined, leads) },
  };
}
